'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Button from '@/components/ui/Button';
import PagerIndicator from '@/components/ui/PagerIndicator';
import useStrapiBanner from '@/hooks/useStrapiBanner';
import { useResponsive } from '@/hooks/useResponsive';
import { getButtonPosition } from '@/utils/bannerButtonPosition';
import { getBannerTextContainerStyles, getFontClass, getBannerHorizontalSpacing } from '@/utils/bannerTextPosition';
import { getObjetoSuperiorPosition, isObjetoSuperiorEscalado, getObjetoSuperiorEscaladoInnerStyle, getObjetoSuperiorObjectPosition, getObjetoFondoAjuste, getObjetoFondoPositionResolved } from '@/utils/bannerObjectPosition';

interface HeroSectionProps {
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ className = '' }) => {
  const [currentHeroPage, setCurrentHeroPage] = useState(1);
  const [currentRightPage, setCurrentRightPage] = useState(1);
  const { isMobile } = useResponsive();
  
  // Hook para todos los banners desde nueva API
  const { data: bannerData, loading: bannerLoading, error: bannerError } = useStrapiBanner();

  // Obtener banners según dispositivo
  const leftBanners = isMobile 
    ? bannerData?.bannerIzquierdaMobile || []
    : bannerData?.bannerIzquierdaDesktop || [];
  
  const rightBanners = isMobile
    ? bannerData?.bannerDerechaMobile || []
    : bannerData?.bannerDerechaDesktop || [];
  
  const totalPages = leftBanners.length || 1;
  const totalRightPages = rightBanners.length || 1;

  // Carrusel automático para banner izquierdo cada 5 segundos
  useEffect(() => {
    if (totalPages > 1) {
      const interval = setInterval(() => {
        setCurrentHeroPage((prev) => (prev % totalPages) + 1);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [totalPages]);

  // Carrusel automático para banner derecho cada 4.5 segundos
  useEffect(() => {
    if (totalRightPages > 1) {
      const interval = setInterval(() => {
        setCurrentRightPage((prev) => (prev % totalRightPages) + 1);
      }, 4500);

      return () => clearInterval(interval);
    }
  }, [totalRightPages]);

  // Obtener el banner izquierdo actual desde nueva API
  const currentLeftBanner = leftBanners[currentHeroPage - 1];

  // Obtener el banner derecho actual desde nueva API
  const currentRightBanner = rightBanners[currentRightPage - 1];

  // Función para renderizar el texto dinámico del banner izquierdo
  const renderLeftBannerText = () => {
    if (!currentLeftBanner || (!currentLeftBanner.titulo && !currentLeftBanner.descripcion)) {
      return null;
    }

    const containerStyles = getBannerTextContainerStyles(
      currentLeftBanner.alineacionTitulo,
      currentLeftBanner.posicionTitulo
    );

    const fontClass = getFontClass(currentLeftBanner.fuente);

    return (
      <div data-component-id="src/components/sections/HeroSection.tsx:78:6" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="78" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={containerStyles}>
        {currentLeftBanner.titulo && (
          <p data-component-id="src/components/sections/HeroSection.tsx:80:10" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="80" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
            className={`${fontClass} text-[40px] leading-[47px] text-global-1 mb-4`}
            style={{ 
              whiteSpace: 'pre-line',
              textAlign: containerStyles.textAlign as any,
              ...(currentLeftBanner.colorTitulo && { color: currentLeftBanner.colorTitulo })
            }}
          >
            {currentLeftBanner.titulo}
          </p>
        )}
        {currentLeftBanner.descripcion && (
          <p data-component-id="src/components/sections/HeroSection.tsx:92:10" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="92" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-medium%20text-xl%20leading-6%20text-global-1%22%7D" 
            className="font-tt-norms-medium text-xl leading-6 text-global-1"
            style={{ 
              whiteSpace: 'pre-line',
              textAlign: containerStyles.textAlign as any,
              maxWidth: '600px',
              ...(currentLeftBanner.colorDescripcion && { color: currentLeftBanner.colorDescripcion })
            }}
          >
            {currentLeftBanner.descripcion}
          </p>
        )}
        {currentLeftBanner.boton && currentLeftBanner.posicionBoton === 'Con texto' && (
          <div data-component-id="src/components/sections/HeroSection.tsx:105:10" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="105" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%22%7D" className="mt-4">
            <Button data-component-id="src/components/sections/HeroSection.tsx:106:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="106" data-component-file="HeroSection.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22px-4%20py-2%20z-20%22%7D"
              variant="primary"
              onClick={() => {
                if (currentLeftBanner.url) {
                  window.location.href = currentLeftBanner.url;
                }
              }}
              className="px-4 py-2 z-20"
              style={{
                color: '#FFFFFF',
                backgroundColor: 'var(--global-bg-1)'
              }}
            >
              {currentLeftBanner.textoBoton || 'Ver más'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Botón del banner izquierdo por cuadrante (cuando no es "Con texto"). Cuadrantes impares usan el mismo margen que el texto.
  const renderLeftBannerButton = () => {
    if (!currentLeftBanner || !currentLeftBanner.boton || currentLeftBanner.posicionBoton === 'Con texto') {
      return null;
    }
    const leftMargin = getBannerHorizontalSpacing(currentLeftBanner.posicionTitulo);
    const buttonPosition = getButtonPosition(currentLeftBanner.posicionBoton, { leftMargin });

    return (
      <Button data-component-id="src/components/sections/HeroSection.tsx:136:6" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="136" data-component-file="HeroSection.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22px-4%20py-2%20z-20%22%7D"
        variant="primary"
        onClick={() => {
          if (currentLeftBanner.url) {
            window.location.href = currentLeftBanner.url;
          }
        }}
        className="px-4 py-2 z-20"
        style={{
          position: 'absolute',
          ...buttonPosition,
          color: '#FFFFFF',
          backgroundColor: 'var(--global-bg-1)'
        }}
      >
        {currentLeftBanner.textoBoton || 'Ver más'}
      </Button>
    );
  };

  // Función para renderizar el texto dinámico del banner derecho
  const renderRightBannerText = () => {
    if (!currentRightBanner || (!currentRightBanner.titulo && !currentRightBanner.descripcion)) {
      return null;
    }

    const containerStyles = getBannerTextContainerStyles(
      currentRightBanner.alineacionTitulo,
      currentRightBanner.posicionTitulo
    );

    const fontClass = getFontClass(currentRightBanner.fuente);
    const isFirstRightSlide = currentRightPage === 1;
    const firstRightBanner = rightBanners[0];

    const titleClassName = `${fontClass} text-[32px] leading-[38px] text-global-1 mb-4`;
    const titleStyle = {
      whiteSpace: 'pre-line' as const,
      textAlign: containerStyles.textAlign as React.CSSProperties['textAlign'],
      maxWidth: '280px',
      ...(currentRightBanner.colorTitulo && { color: currentRightBanner.colorTitulo }),
    };

    return (
      <>
        {!isFirstRightSlide && firstRightBanner && (firstRightBanner.titulo || firstRightBanner.descripcion) && (
          <div data-component-id="src/components/sections/HeroSection.tsx:182:10" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="182" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22sr-only%22%7D" className="sr-only" aria-hidden="true">
            {firstRightBanner.titulo && <h1 data-component-id="src/components/sections/HeroSection.tsx:183:40" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="183" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%7D">{firstRightBanner.titulo}</h1>}
            {firstRightBanner.descripcion && <p data-component-id="src/components/sections/HeroSection.tsx:184:45" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="184" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D">{firstRightBanner.descripcion}</p>}
          </div>
        )}
        <div data-component-id="src/components/sections/HeroSection.tsx:187:8" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="187" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={containerStyles}>
          {currentRightBanner.titulo && isFirstRightSlide && (
            <h1 data-component-id="src/components/sections/HeroSection.tsx:189:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="189" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22%5Bvar%3AtitleClassName%5D%22%7D" className={titleClassName} style={titleStyle}>
              {currentRightBanner.titulo}
            </h1>
          )}
          {currentRightBanner.titulo && !isFirstRightSlide && (
            <p data-component-id="src/components/sections/HeroSection.tsx:194:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="194" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22%5Bvar%3AtitleClassName%5D%22%7D" className={titleClassName} style={titleStyle}>
              {currentRightBanner.titulo}
            </p>
          )}
          {currentRightBanner.descripcion && (
            <p data-component-id="src/components/sections/HeroSection.tsx:199:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="199" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-medium%20text-xl%20leading-6%20text-global-1%22%7D"
              className="font-tt-norms-medium text-xl leading-6 text-global-1"
              style={{
                whiteSpace: 'pre-line',
                textAlign: containerStyles.textAlign as React.CSSProperties['textAlign'],
                maxWidth: '280px',
                ...(currentRightBanner.colorDescripcion && { color: currentRightBanner.colorDescripcion }),
              }}
            >
              {currentRightBanner.descripcion}
            </p>
          )}
        </div>
      </>
    );
  };

  // Función para renderizar el botón del banner derecho
  const renderRightBannerButton = () => {
    if (!currentRightBanner || !currentRightBanner.boton) {
      return null;
    }

    const leftMargin = getBannerHorizontalSpacing(currentRightBanner.posicionTitulo);
    const buttonPosition = getButtonPosition(currentRightBanner.posicionBoton, { leftMargin });

    return (
      <Button data-component-id="src/components/sections/HeroSection.tsx:226:6" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="226" data-component-file="HeroSection.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22px-4%20py-2%20z-20%22%7D"
        variant="primary"
        onClick={() => {
          if (currentRightBanner.url) {
            window.location.href = currentRightBanner.url;
          }
        }}
        className="px-4 py-2 z-20"
        style={{
          position: 'absolute',
          ...buttonPosition,
          color: '#FFFFFF',
          backgroundColor: 'var(--global-bg-1)'
        }}
      >
        {currentRightBanner.textoBoton || 'Ver más'}
      </Button>
    );
  };

  return (
    <div data-component-id="src/components/sections/HeroSection.tsx:247:4" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="247" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
      <div data-component-id="src/components/sections/HeroSection.tsx:248:6" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="248" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20gap-x-8%20justify-center%20w-full%22%7D" className="flex flex-row gap-x-8 justify-center w-full">
        {/* Main Hero Banner Izquierdo - Desde nueva API */}
        <div data-component-id="src/components/sections/HeroSection.tsx:250:8" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="250" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B20px%5D%20flex-1%20max-w-%5B846px%5D%20h-%5B507px%5D%20relative%20overflow-hidden%22%7D"
          className="rounded-[20px] flex-1 max-w-[846px] h-[507px] relative overflow-hidden"
          style={{ backgroundColor: currentLeftBanner?.colorFondoHex || '#8FD1C7' }}
        >
          {bannerLoading ? (
            <div data-component-id="src/components/sections/HeroSection.tsx:255:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="255" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <div data-component-id="src/components/sections/HeroSection.tsx:256:14" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="256" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20text-global-1%22%2C%22textContent%22%3A%22Cargando%20banner...%22%7D" className="animate-pulse text-global-1">Cargando banner...</div>
            </div>
          ) : bannerError ? (
            <div data-component-id="src/components/sections/HeroSection.tsx:259:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="259" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <div data-component-id="src/components/sections/HeroSection.tsx:260:14" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="260" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-red-500%20text-sm%22%2C%22textContent%22%3A%22Error%3A%22%7D" className="text-red-500 text-sm">Error: {bannerError}</div>
            </div>
          ) : currentLeftBanner ? (
            <>
              {/* Objeto_fondo o Pieza_grafica como imagen de fondo */}
              {(currentLeftBanner.objetoFondo || currentLeftBanner.piezaGrafica) && (
                <Image data-component-id="src/components/sections/HeroSection.tsx:266:16" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="266" data-component-file="HeroSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%7D"
                  src={(currentLeftBanner.objetoFondo || currentLeftBanner.piezaGrafica)!.url}
                  alt={(currentLeftBanner.objetoFondo || currentLeftBanner.piezaGrafica)!.alternativeText || 'Banner'}
                  fill
                  className={`${getObjetoFondoAjuste(currentLeftBanner.objetoFondoAjuste || (currentLeftBanner.piezaGrafica ? 'sangrado' : 'ajustado'))} rounded-[20px]`}
                  style={{
                    objectPosition: getObjetoFondoPositionResolved(currentLeftBanner.objetoFondoAjuste, currentLeftBanner.objetoFondoPosicion)
                  }}
                  priority={currentHeroPage === 1}
                  sizes="(max-width: 846px) 100vw, 846px"
                />
              )}
              {/* Texto dinámico (Título, Descripción y Botón si "Con texto") */}
              {renderLeftBannerText()}
              {/* Botón por cuadrante (cuando no es "Con texto"; cuadrantes impares con margen del texto) */}
              {renderLeftBannerButton()}
              {/* Objeto_superior */}
              {currentLeftBanner.objetoSuperior && (
                <div data-component-id="src/components/sections/HeroSection.tsx:284:16" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="284" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20z-%5B1%5D%22%7D"
                  className="absolute z-[1]"
                  style={{
                    ...getObjetoSuperiorPosition(currentLeftBanner.objetoSuperiorPosicion),
                    margin: 0,
                    padding: 0,
                    ...(isObjetoSuperiorEscalado(currentLeftBanner.objetoSuperiorPosicion) ? { overflow: 'visible' } : {}),
                  }}
                >
                  {isObjetoSuperiorEscalado(currentLeftBanner.objetoSuperiorPosicion) ? (
                    <div data-component-id="src/components/sections/HeroSection.tsx:294:20" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="294" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={getObjetoSuperiorEscaladoInnerStyle(currentLeftBanner.objetoSuperiorPosicion)}>
                      <Image data-component-id="src/components/sections/HeroSection.tsx:295:22" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="295" data-component-file="HeroSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%22%7D"
                        src={currentLeftBanner.objetoSuperior.url}
                        alt={currentLeftBanner.objetoSuperior.alternativeText || 'Objeto superior'}
                        fill
                        className="object-contain"
                        style={{
                          objectPosition: getObjetoSuperiorObjectPosition(currentLeftBanner.objetoSuperiorPosicion),
                        }}
                        sizes="33vw"
                      />
                    </div>
                  ) : (
                    <Image data-component-id="src/components/sections/HeroSection.tsx:307:20" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="307" data-component-file="HeroSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%22%7D"
                      src={currentLeftBanner.objetoSuperior.url}
                      alt={currentLeftBanner.objetoSuperior.alternativeText || 'Objeto superior'}
                      width={currentLeftBanner.objetoSuperior.width}
                      height={currentLeftBanner.objetoSuperior.height}
                      className="object-contain"
                      style={{
                        display: 'block',
                        margin: 0,
                        padding: 0,
                      }}
                    />
                  )}
                </div>
              )}
              {/* Pager Indicator */}
              {totalPages > 1 && (
                <div data-component-id="src/components/sections/HeroSection.tsx:324:16" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="324" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-%5B45px%5D%20left-1%2F2%20transform%20-translate-x-1%2F2%20z-10%22%7D" className="absolute bottom-[45px] left-1/2 transform -translate-x-1/2 z-10">
                  <PagerIndicator data-component-id="src/components/sections/HeroSection.tsx:325:18" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="325" data-component-file="HeroSection.tsx" data-component-name="PagerIndicator" data-component-content="%7B%22elementName%22%3A%22PagerIndicator%22%7D"
                    totalPages={totalPages}
                    currentPage={currentHeroPage}
                    onPageChange={setCurrentHeroPage}
                  />
                </div>
              )}
            </>
          ) : (
            <div data-component-id="src/components/sections/HeroSection.tsx:334:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="334" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <p data-component-id="src/components/sections/HeroSection.tsx:335:14" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="335" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-global-1%22%2C%22textContent%22%3A%22No%20hay%20banners%20disponibles%22%7D" className="text-global-1">No hay banners disponibles</p>
            </div>
          )}
        </div>
        
        {/* Right Banner - Desde nueva API */}
        <div data-component-id="src/components/sections/HeroSection.tsx:341:8" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="341" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B20px%5D%20flex-1%20max-w-%5B532px%5D%20h-%5B507px%5D%20relative%20overflow-hidden%22%7D" 
          className="rounded-[20px] flex-1 max-w-[532px] h-[507px] relative overflow-hidden"
          style={{ backgroundColor: currentRightBanner?.colorFondoHex || '#8FD1C7' }}
        >
          {bannerLoading ? (
            <div data-component-id="src/components/sections/HeroSection.tsx:346:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="346" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <div data-component-id="src/components/sections/HeroSection.tsx:347:14" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="347" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20text-global-1%22%2C%22textContent%22%3A%22Cargando%20banner...%22%7D" className="animate-pulse text-global-1">Cargando banner...</div>
            </div>
          ) : bannerError ? (
            <div data-component-id="src/components/sections/HeroSection.tsx:350:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="350" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <div data-component-id="src/components/sections/HeroSection.tsx:351:14" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="351" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-red-500%20text-sm%22%2C%22textContent%22%3A%22Error%3A%22%7D" className="text-red-500 text-sm">Error: {bannerError}</div>
            </div>
          ) : currentRightBanner ? (
            <>
              {/* Objeto_fondo o Pieza_grafica como imagen de fondo */}
              {(currentRightBanner.objetoFondo || currentRightBanner.piezaGrafica) && (
                <Image data-component-id="src/components/sections/HeroSection.tsx:357:16" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="357" data-component-file="HeroSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%7D"
                  src={(currentRightBanner.objetoFondo || currentRightBanner.piezaGrafica)!.url}
                  alt={(currentRightBanner.objetoFondo || currentRightBanner.piezaGrafica)!.alternativeText || 'Banner'}
                  fill
                  className={`${getObjetoFondoAjuste(currentRightBanner.objetoFondoAjuste || (currentRightBanner.piezaGrafica ? 'sangrado' : 'ajustado'))} rounded-[20px]`}
                  style={{
                    objectPosition: getObjetoFondoPositionResolved(currentRightBanner.objetoFondoAjuste, currentRightBanner.objetoFondoPosicion)
                  }}
                  priority={currentRightPage === 1}
                  sizes="(max-width: 532px) 100vw, 532px"
                />
              )}
              {/* Texto dinámico (Título y Descripción) */}
              {renderRightBannerText()}
              {/* Botón posicionado según cuadrante */}
              {renderRightBannerButton()}
              {/* Objeto_superior */}
              {currentRightBanner.objetoSuperior && (
                <div data-component-id="src/components/sections/HeroSection.tsx:375:16" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="375" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20z-%5B1%5D%22%7D"
                  className="absolute z-[1]"
                  style={{
                    ...getObjetoSuperiorPosition(currentRightBanner.objetoSuperiorPosicion),
                    margin: 0,
                    padding: 0,
                    ...(isObjetoSuperiorEscalado(currentRightBanner.objetoSuperiorPosicion) ? { overflow: 'visible' } : {}),
                  }}
                >
                  {isObjetoSuperiorEscalado(currentRightBanner.objetoSuperiorPosicion) ? (
                    <div data-component-id="src/components/sections/HeroSection.tsx:385:20" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="385" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={getObjetoSuperiorEscaladoInnerStyle(currentRightBanner.objetoSuperiorPosicion)}>
                      <Image data-component-id="src/components/sections/HeroSection.tsx:386:22" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="386" data-component-file="HeroSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%22%7D"
                        src={currentRightBanner.objetoSuperior.url}
                        alt={currentRightBanner.objetoSuperior.alternativeText || 'Objeto superior'}
                        fill
                        className="object-contain"
                        style={{
                          objectPosition: getObjetoSuperiorObjectPosition(currentRightBanner.objetoSuperiorPosicion),
                        }}
                        sizes="33vw"
                      />
                    </div>
                  ) : (
                    <Image data-component-id="src/components/sections/HeroSection.tsx:398:20" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="398" data-component-file="HeroSection.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%22%7D"
                      src={currentRightBanner.objetoSuperior.url}
                      alt={currentRightBanner.objetoSuperior.alternativeText || 'Objeto superior'}
                      width={currentRightBanner.objetoSuperior.width}
                      height={currentRightBanner.objetoSuperior.height}
                      className="object-contain"
                      style={{
                        display: 'block',
                        margin: 0,
                        padding: 0,
                      }}
                    />
                  )}
                </div>
              )}
              {/* Pager Indicator */}
              {totalRightPages > 1 && (
                <div data-component-id="src/components/sections/HeroSection.tsx:415:16" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="415" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-%5B45px%5D%20left-1%2F2%20transform%20-translate-x-1%2F2%20z-10%22%7D" className="absolute bottom-[45px] left-1/2 transform -translate-x-1/2 z-10">
                  <PagerIndicator data-component-id="src/components/sections/HeroSection.tsx:416:18" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="416" data-component-file="HeroSection.tsx" data-component-name="PagerIndicator" data-component-content="%7B%22elementName%22%3A%22PagerIndicator%22%7D"
                    totalPages={totalRightPages}
                    currentPage={currentRightPage}
                    onPageChange={setCurrentRightPage}
                  />
                </div>
              )}
            </>
          ) : (
            <div data-component-id="src/components/sections/HeroSection.tsx:425:12" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="425" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <p data-component-id="src/components/sections/HeroSection.tsx:426:14" data-component-path="src/components/sections/HeroSection.tsx" data-component-line="426" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-global-1%22%2C%22textContent%22%3A%22No%20hay%20banners%20disponibles%22%7D" className="text-global-1">No hay banners disponibles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
